import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryButton from '../components/PrimaryButton';
import { LockClosedIcon, UserIcon } from '@heroicons/react/24/outline';

const Login = () => {
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCredentials(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:8080');

        try {
            const response = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Login failed');
            }

            localStorage.setItem('token', data.access_token);
            localStorage.setItem('refresh_token', data.refresh_token);
            navigate('/admin');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [credentials, navigate]);

    return (
        <main className="min-h-screen bg-earth-50 dark:bg-earth-900 flex items-center justify-center px-4 pt-20">
            <div className="w-full max-w-md bg-white dark:bg-earth-800 rounded-2xl shadow-xl p-8 border border-earth-100 dark:border-earth-700">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-earth-900 dark:text-earth-50">Admin Access</h1>
                    <p className="text-earth-600 dark:text-earth-200 mt-2 text-sm">Please log in to manage your portfolio</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-earth-700 dark:text-earth-200 mb-2">
                            Username
                        </label>
                        <div className="relative">
                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-earth-400">
                                <UserIcon className="h-5 w-5" />
                            </span>
                            <input
                                type="text"
                                name="username"
                                required
                                value={credentials.username}
                                onChange={handleChange}
                                className="block w-full pl-10 pr-3 py-2 border border-earth-200 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-900 dark:text-earth-50 focus:ring-2 focus:ring-earth-500 focus:border-transparent transition-all outline-none"
                                placeholder="Enter username"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-earth-700 dark:text-earth-200 mb-2">
                            Password
                        </label>
                        <div className="relative">
                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-earth-400">
                                <LockClosedIcon className="h-5 w-5" />
                            </span>
                            <input
                                type="password"
                                name="password"
                                required
                                value={credentials.password}
                                onChange={handleChange}
                                className="block w-full pl-10 pr-3 py-2 border border-earth-200 dark:border-earth-700 rounded-xl bg-earth-50 dark:bg-earth-900 text-earth-900 dark:text-earth-50 focus:ring-2 focus:ring-earth-500 focus:border-transparent transition-all outline-none"
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="text-red-500 text-sm text-center font-medium">
                            {error}
                        </div>
                    )}

                    <PrimaryButton 
                        type="submit" 
                        className="w-full justify-center disabled:opacity-50"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Authenticating...' : 'Sign In'}
                    </PrimaryButton>
                </form>
            </div>
        </main>
    );
};

export default Login;
